import type { EditorState } from "@tiptap/pm/state";
import type { SystemConfig } from "@/state/configState";
import type {
  ProtocolExport,
  ProtocolExportParameterValueLabware,
} from "./protocolExport";
import type { LabwareObject } from "@/automation/labware/labwareManager";
import {
  exportProtocolBase,
  createDeckLayoutFromConfig,
} from "@/automation/protocolExport";
import {
  calculateRequiredTipLabware,
  assignLabwareToDeck,
  resolveTipTypesFromVolume,
} from "@/automation/utils/deckLayoutUtils";
import type { DeckConfiguration } from "@/state/run/useDeckLayoutState";
import { getLabwareTypeDimensionString, parseDimensionString } from "@/automation/utils/labwareDimensions";
import { MDB_ID_FALLBACK } from "@/automation/mdbIdFallback";
import {
  isTipBox,
  isTipRack,
  isReturnVesselForTip,
} from "@/automation/utils/labwareClassification";

/**
 * Takes a base ProtocolExport and adds tip parameters and tip labware,
 * using calculateRequiredTipLabware to determine which tips go with which source.
 */
function decorateProtocolWithTips(
  base: ProtocolExport,
  state: EditorState,
  activeConfig: SystemConfig
): ProtocolExport {
  const { tipLabware, sourceToTipMap, stepToTipMap, stepTipUsageByUid } =
    calculateRequiredTipLabware(state, activeConfig);

  // Ensure base.labware is an array before spreading
  const baseLabware = Array.isArray(base.labware) ? base.labware : [];

  const combinedLabware = [
    ...baseLabware,
    ...tipLabware.filter((lw) => !baseLabware.some((b) => b.id === lw.id)),
  ];

  const tipLookup = new Map(tipLabware.map((lw) => [lw.id, lw]));

  let stepIdx = -1;
  const newSteps = base.steps.map((step) => {
    stepIdx += 1;
    const configDef = activeConfig.steps.find(
      (s) => s.id === step.configId || s.id === step.id || s.name === step.name
    );
    if (!configDef) return step;

    const tipsDef = configDef.params.find((p) => p.role === "tips");
    if (!tipsDef) return step;

    // Get the required tip type for this step from the config
    const requiredTipTypeId =
      tipsDef.options && tipsDef.options.length > 0 ? tipsDef.options[0] : null;

    if (!requiredTipTypeId) {
      return step; // No tip type defined for this step
    }

    const sourceDef = configDef.params.find((p) => p.role === "source");
    let sourceId: string | undefined;
    if (sourceDef) {
      const inst = step.parameters.find((p) => p.name === sourceDef.name);
      if (
        inst &&
        typeof inst.value === "object" &&
        inst.value !== null && // Add null check
        "type" in inst.value && // Type guard
        inst.value.type === "labware" &&
        inst.value.labware // Check if labware exists
      ) {
        sourceId = inst.value.labware.id;
      }
    }
    if (!sourceId) {
      return step;
    }

    // Prefer direct step mapping, fallback to composite key using source
    let tipId = stepToTipMap.get(stepIdx);
    if (!tipId && sourceId) {
      const compositeKey = `${sourceId}:${requiredTipTypeId}`;
      tipId = sourceToTipMap.get(compositeKey);
    }
    if (!tipId) return step;

    const tipLabwareObject = tipLookup.get(tipId);
    if (!tipLabwareObject) return step;

    // Ensure step.parameters is an array
    const stepParams = Array.isArray(step.parameters) ? step.parameters : [];

    const newParams = [
      ...stepParams,
      {
        name: tipsDef.name,
        id: tipsDef.id,
        value: { type: "labware" as const, labware: tipLabwareObject },
      },
    ];

    return {
      ...step,
      parameters: newParams,
    };
  });

  // Attach per-step tip usage for display on the tip allocation screen. Keyed by
  // step uid so it stays correct even when custom blocks (skipped above) offset
  // the document step order.
  const stepsWithUsage = newSteps.map((step) => ({
    ...step,
    tipUsage: step.uid ? stepTipUsageByUid.get(step.uid) : undefined,
  }));

  return {
    ...base,
    steps: stepsWithUsage,
    labware: combinedLabware,
  };
}

/**
 * Iterates through protocol steps and adds the requiredTipTypeIds to each step
 * based on the 'tips' role parameter in the system configuration and volume requirements.
 */
export function decorateStepsWithRequiredTipType(
  base: ProtocolExport,
  activeConfig: SystemConfig
): ProtocolExport {
  const newSteps = base.steps.map((step) => {
    const configDef = activeConfig.steps.find(
      (s) => s.id === step.configId || s.id === step.id || s.name === step.name
    );
    if (!configDef) return step;

    const tipsDef = configDef.params.find((p) => p.role === "tips");
    if (!tipsDef) return step;

    // Start with all available tip types as default
    let requiredTipTypeIds: string[] = tipsDef.options || [];

    // Only use volume-based selection if volume options exist and volume parameter is found
    if (tipsDef.volumeOptions && tipsDef.volumeOptions.length > 0) {
      const volumeParams = configDef.params.filter((p) => p.type === "volume");
      if (volumeParams.length > 0) {
        // Extract volume value from step instance
        const stepParams = Array.isArray(step.parameters)
          ? step.parameters
          : [];
        let volumeValue: number | null = null;

        for (const param of stepParams) {
          if (
            volumeParams.some(
              (vp) => vp.name.toLowerCase() === param.name.toLowerCase()
            )
          ) {
            if (typeof param.value === "number") {
              volumeValue = param.value;
              break;
            }
          }
        }

        // Use volume-based selection only if we found a valid volume
        if (volumeValue !== null) {
          const fallbackTipType = tipsDef.options?.[0] || "";
          requiredTipTypeIds = resolveTipTypesFromVolume(
            volumeValue,
            tipsDef.volumeOptions,
            fallbackTipType
          );
        }
      }
    }

    // If still no tip types resolved, check for attached tip type
    if (requiredTipTypeIds.length === 0) {
      const stepParams = Array.isArray(step.parameters) ? step.parameters : [];
      const attachedTipsParam = stepParams.find((p) => p.name === tipsDef.name);

      if (
        attachedTipsParam &&
        typeof attachedTipsParam.value === "object" &&
        attachedTipsParam.value !== null &&
        "labware" in attachedTipsParam.value
      ) {
        const tipTypeId = (attachedTipsParam.value as any).labware.typeId;
        requiredTipTypeIds = [tipTypeId];
      }
    }

    return {
      ...step,
      requiredTipTypeIds,
    };
  });

  return {
    ...base,
    steps: newSteps,
  };
}

/**
 * Ensures steps have a liquid_class parameter when defined in config.
 * If missing in the protocol, injects with config default or first option.
 */
export function decorateStepsWithDefaultLiquidClass(
  base: ProtocolExport,
  activeConfig: SystemConfig
): ProtocolExport {
  const newSteps = base.steps.map((step) => {
    const configDef = activeConfig.steps.find(
      (s) => s.id === step.configId || s.id === step.id || s.name === step.name
    );
    if (!configDef) return step;

    const lcParamDef = configDef.params.find((p) => p.id === "liquid_class");
    if (!lcParamDef) return step;

    const hasLiquidClass = step.parameters.some((p) => p.id === "liquid_class");
    if (hasLiquidClass) return step;

    // pick default or first option
    const defaultValue =
      lcParamDef.default ||
      (lcParamDef.options && lcParamDef.options[0]) ||
      "aqueous";

    return {
      ...step,
      parameters: [
        ...step.parameters,
        {
          id: "liquid_class",
          name: lcParamDef.name || "Liquid class",
          value: String(defaultValue),
        },
      ],
    };
  });

  return { ...base, steps: newSteps };
}

/**
 * Generic decorator that auto-fills ALL hidden labware parameters.
 * Finds config params with `hidden === true && type === "labware"`,
 * picks the first option from config, and injects into the export.
 * Replaces the old role-specific decorators (adapter, waste, trash).
 */
function decorateProtocolWithHiddenLabware(
  base: ProtocolExport,
  activeConfig: SystemConfig
): ProtocolExport {
  const initialLabware = Array.isArray(base.labware) ? base.labware : [];
  let combinedLabware: LabwareObject[] = [...initialLabware];

  const newSteps = base.steps.map((step) => {
    const configDef = activeConfig.steps.find(
      (s) => s.id === step.configId || s.id === step.id || s.name === step.name
    );
    if (!configDef) return step;

    const hiddenLabwareParams = configDef.params.filter(
      (p) => p.hidden === true && p.type === "labware"
    );
    if (hiddenLabwareParams.length === 0) return step;

    const stepParams = Array.isArray(step.parameters) ? step.parameters : [];
    let updatedParams = [...stepParams];

    for (const paramDef of hiddenLabwareParams) {
      // Skip if already present (e.g. tips filled by tip decorator)
      if (updatedParams.some((p) => p.name === paramDef.name || p.id === paramDef.id)) {
        continue;
      }

      const resolvedOptions = paramDef.options ?? [];

      if (resolvedOptions.length === 0) {
        console.warn(
          `decorateProtocolWithHiddenLabware: Step '${
            step.name || step.id
          }' has hidden labware param '${
            paramDef.name
          }' (id: ${paramDef.id}) but no options defined. Skipping.`
        );
        continue;
      }

      // For the trash role, default to a real waste device over a tip-return box
      // so a freshly-built protocol defaults to "drop in waste" — the operator
      // opts into returning tips by picking a box on the tip-allocation screen.
      // When ONLY return targets are enabled (no waste device), the first one is
      // used, and syncTipReturn turns it into a tip_return below.
      const firstOptionTypeId =
        paramDef.role === "trash"
          ? (resolvedOptions.find(
              (id) => !isTipBox(id, activeConfig) && !isTipRack(id, activeConfig),
            ) ?? resolvedOptions[0])
          : resolvedOptions[0];
      const labwareTypeDetails = activeConfig.labware?.find(
        (lt: { id: string; label: string; [key: string]: any }) =>
          lt.id === firstOptionTypeId
      );

      if (!labwareTypeDetails) {
        console.warn(
          `decorateProtocolWithHiddenLabware: Step '${
            step.name || step.id
          }' hidden labware param '${
            paramDef.name
          }' references labware type '${firstOptionTypeId}' not found in config. Skipping.`
        );
        continue;
      }

      const labwareObject: LabwareObject = {
        id: `${labwareTypeDetails.id}-${paramDef.id ?? '1'}`,
        typeId: labwareTypeDetails.id,
        label: labwareTypeDetails.label,
      };

      if (!combinedLabware.some((lw) => lw.id === labwareObject.id)) {
        combinedLabware.push(labwareObject);
      }

      updatedParams.push({
        name: paramDef.name,
        id: paramDef.id,
        value: { type: "labware" as const, labware: labwareObject },
      });
    }

    // tip_return is derived later, from the operator's per-step trash choice on
    // the tip-allocation screen — see syncTipReturn(). The decorator only fills
    // the default trash vessel above.

    return {
      ...step,
      parameters: updatedParams,
    };
  });

  return {
    ...base,
    steps: newSteps,
    labware: combinedLabware,
  };
}

/**
 * Build the runner-facing labwareConfig entry for one typeId from the active
 * config's labware catalog metadata. Returns undefined when the type isn't in
 * the config. Shared by decorateProtocolWithLabwareConfig and syncTipReturn so a
 * tip-return box gets its tip-stand into the payload even when its typeId isn't
 * otherwise referenced as a tip.
 */
function buildLabwareConfigEntry(
  typeId: string,
  activeConfig: SystemConfig,
): import("./protocolExport").ProtocolLabwareConfig | undefined {
  const entry = activeConfig.labware?.find((l) => l.id === typeId);
  if (!entry) return undefined;

  const specsRecord: Record<string, string | number | boolean> = {};
  for (const spec of entry.specifications) {
    specsRecord[spec.name] = spec.value;
  }

  return {
    typeId,
    label: entry.label,
    mdbId: entry.mdbId ?? MDB_ID_FALLBACK[typeId] ?? 0,
    category: entry.category ?? "plate",
    rows: entry.rows ?? deriveRowsFromSpecs(specsRecord) ?? 8, // LEGACY_COMPAT
    tipStand: entry.tipStand,
    adapterType: entry.adapterType,
    adapterStand: entry.adapterStand,
    specifications: specsRecord,
  };
}

/** Pull the labware typeId out of a (single) labware param value, if any. */
function paramLabwareTypeId(value: unknown): string | undefined {
  if (
    value &&
    typeof value === "object" &&
    !Array.isArray(value) &&
    "type" in value &&
    (value as any).type === "labware" &&
    "labware" in value
  ) {
    return (value as any).labware?.typeId;
  }
  return undefined;
}

/**
 * The trash-role vessels compatible with a step's selected tips: every waste
 * device (always valid), plus any tip-return box/rack whose tips match the
 * selected tip by mdb_id. Shared by the tip-allocation picker and syncTipReturn.
 */
export function compatibleReturnVesselTypeIds(
  trashOptionTypeIds: string[],
  selectedTipTypeId: string | undefined,
  activeConfig: SystemConfig,
): string[] {
  return trashOptionTypeIds.filter((id) => {
    const isReturnBox = isTipBox(id, activeConfig) || isTipRack(id, activeConfig);
    if (!isReturnBox) return true; // waste device — always usable
    return (
      !!selectedTipTypeId &&
      isReturnVesselForTip(id, selectedTipTypeId, activeConfig)
    );
  });
}

/**
 * Reconciles each step's `trash` choice into the runner contract:
 *  - When the chosen vessel is an empty tip box/rack, point both `trash` and a
 *    `tip_return` param at ONE shared empty-box instance (`<typeId>-return`,
 *    relabeled "(Empty - Return)"). The runner's ReturnTipTracker then places
 *    used tips back into it one column/row at a time.
 *  - When it's a waste device, drop any `tip_return` param so tips go to waste.
 *
 * Also repairs a now-incompatible choice (e.g. the operator changed the step's
 * tips after picking a return box) by falling back to a waste device, else the
 * first compatible vessel. Called after the tip-allocation screen and at export
 * init, before the deck layout is built. Idempotent.
 */
export function syncTipReturn(
  protocol: ProtocolExport,
  activeConfig: SystemConfig
): void {
  const desiredTrashLabware = new Map<string, LabwareObject>();

  for (const step of protocol.steps) {
    const configDef = activeConfig.steps.find(
      (s) => s.id === step.configId || s.id === step.id || s.name === step.name
    );
    if (!configDef) continue;

    const trashDef = configDef.params.find((p) => p.role === "trash");
    if (!trashDef) continue;

    const trashParam = step.parameters?.find(
      (p) => p.id === trashDef.id || p.name === trashDef.name
    );
    if (!trashParam) continue;

    let chosenTypeId = paramLabwareTypeId(trashParam.value);
    if (!chosenTypeId) continue;

    // Selected tip type for this step (drives return-vessel compatibility).
    const tipsDef = configDef.params.find((p) => p.role === "tips");
    const tipsParam = tipsDef
      ? step.parameters?.find(
          (p) => p.id === tipsDef.id || p.name === tipsDef.name
        )
      : undefined;
    const selectedTipTypeId = paramLabwareTypeId(tipsParam?.value);

    // Repair a stale/incompatible choice against the live options.
    const compatible = compatibleReturnVesselTypeIds(
      trashDef.options ?? [],
      selectedTipTypeId,
      activeConfig
    );
    if (compatible.length > 0 && !compatible.includes(chosenTypeId)) {
      chosenTypeId =
        compatible.find(
          (id) => !isTipBox(id, activeConfig) && !isTipRack(id, activeConfig)
        ) ?? compatible[0];
    }

    const isReturn =
      isTipBox(chosenTypeId, activeConfig) ||
      isTipRack(chosenTypeId, activeConfig);
    const typeDetails = activeConfig.labware?.find(
      (lt: { id: string; label: string }) => lt.id === chosenTypeId
    );
    const baseLabel = typeDetails?.label ?? chosenTypeId;

    const instanceId = isReturn
      ? `${chosenTypeId}-return`
      : `${chosenTypeId}-trash`;
    const label = isReturn ? `${baseLabel} (Empty - Return)` : baseLabel;
    const labwareObject: LabwareObject = {
      id: instanceId,
      typeId: chosenTypeId,
      label,
    };
    desiredTrashLabware.set(instanceId, labwareObject);

    // Point trash at the (possibly repaired) vessel.
    trashParam.value = { type: "labware", labware: labwareObject };

    // Add / refresh / drop the tip_return param.
    const returnIdx = step.parameters.findIndex((p) => p.id === "tip_return");
    if (isReturn) {
      const tipReturnParam = {
        name: "Tip Return Box",
        id: "tip_return",
        value: { type: "labware" as const, labware: labwareObject },
      };
      if (returnIdx >= 0) step.parameters[returnIdx] = tipReturnParam;
      else step.parameters.push(tipReturnParam);
    } else if (returnIdx >= 0) {
      step.parameters.splice(returnIdx, 1);
    }
  }

  // Rebuild the labware list: keep everything except the trash/return instances
  // we manage, then re-add exactly the ones now in use (deduped by id). This
  // drops orphaned boxes when a choice changes.
  const isManaged = (id: string) =>
    id.endsWith("-trash") || id.endsWith("-return");
  protocol.labware = [
    ...protocol.labware.filter((lw) => !isManaged(lw.id)),
    ...desiredTrashLabware.values(),
  ];

  // The runner resolves a return box's tip-stand from the protocol's
  // labwareConfig (built earlier, from the then-referenced types). A return box
  // whose typeId isn't otherwise used as a tip would be missing — add it so
  // LabwareConfig.getTipStand() resolves on the runner side.
  if (protocol.labwareConfig) {
    for (const lw of desiredTrashLabware.values()) {
      if (!protocol.labwareConfig[lw.typeId]) {
        const entry = buildLabwareConfigEntry(lw.typeId, activeConfig);
        if (entry) protocol.labwareConfig[lw.typeId] = entry;
      }
    }
  }
}

/**
 * Iterates through protocol steps and adds the deckPosition to parameters
 * whose value is a labware object, using the provided layout map.
 *
 * @param protocol The protocol object (ideally after tips/adapters/labware positions are added).
 * @param numericDeckLayout A map of labware IDs to their 1-based numeric deck positions.
 * @returns A new protocol object with deck positions added to relevant parameters.
 */
export function decorateParametersWithPositions(
  protocol: ProtocolExport,
  numericDeckLayout: Record<string, number>
): ProtocolExport {
  const stepsWithParamPositions = protocol.steps.map((step) => ({
    ...step,
    parameters: step.parameters.map((param) => {
      if (param.value && typeof param.value === "object") {
        if (Array.isArray(param.value)) {
          // Handle array of labware values
          const decoratedArrayValue = param.value.map((item) => {
            if (
              item &&
              typeof item === "object" &&
              "type" in item &&
              item.type === "labware" &&
              item.labware?.id &&
              numericDeckLayout[item.labware.id] !== undefined
            ) {
              const labwareId = item.labware.id;
              const position = numericDeckLayout[labwareId];
              return {
                ...(item as ProtocolExportParameterValueLabware),
                deckPosition: position,
              };
            }
            return item; // Return item unchanged if not a placed labware
          });
          return {
            ...param,
            value: decoratedArrayValue,
          };
        } else if (
          "type" in param.value &&
          param.value.type === "labware" &&
          param.value.labware?.id &&
          numericDeckLayout[param.value.labware.id] !== undefined
        ) {
          // Handle single labware value
          const labwareId = param.value.labware.id;
          const position = numericDeckLayout[labwareId];
          return {
            ...param,
            value: {
              ...(param.value as ProtocolExportParameterValueLabware),
              deckPosition: position,
            },
          };
        }
      }
      // If not a placed labware parameter or array of labware, return it unchanged
      return param;
    }),
  }));

  // Return a new protocol object with the updated steps
  return {
    ...protocol,
    steps: stepsWithParamPositions,
  };
}


/**
 * Decorates protocol parameters with replicates.
 * If a parameter definition in activeConfig has a 'replicates' property (string ID of a target labware parameter),
 * the instance of this directive parameter specifies the total number of labware instances.
 * New labware instances are created, added to protocol.labware.
 * The target labware parameter's value in the step becomes an array of labware values if numberOfInstances > 1.
 * The directive parameter is removed from the step.
 */
export function decorateParametersWithReplicates(
  protocol: ProtocolExport,
  activeConfig: SystemConfig
): ProtocolExport {
  const newGlobalLabware: LabwareObject[] = [];

  const newSteps = protocol.steps.map((step) => {
    const configDef = activeConfig.steps.find(
      (s) => s.id === step.configId || s.id === step.id || s.name === step.name
    );
    if (!configDef) {
      return step;
    }

    let currentParameters = [...step.parameters];

    for (const paramDef of configDef.params) {
      if (paramDef.replicates && typeof paramDef.replicates === "string") {
        const replicatesDirectiveParamDef = paramDef;
        const targetLabwareParamDefId = paramDef.replicates;

        const replicatesDirectiveParamInstance = step.parameters.find(
          (p) => p.id === replicatesDirectiveParamDef.id
        );

        if (
          !replicatesDirectiveParamInstance ||
          typeof replicatesDirectiveParamInstance.value !== "number" ||
          replicatesDirectiveParamInstance.value <= 0
        ) {
          console.warn(
            `[decorateParametersWithReplicates] Replicates directive parameter '${
              replicatesDirectiveParamDef.name
            }' in step '${
              step.name || step.id
            }' not found, not a positive number, or zero. Value: ${
              replicatesDirectiveParamInstance?.value
            }`
          );
          continue;
        }
        const numberOfInstances =
          replicatesDirectiveParamInstance.value as number;

        if (numberOfInstances === 1) {
          // Only one instance means no *new* replicates. Target param remains singular.
          continue;
        }

        const targetParamInstanceIndex = currentParameters.findIndex(
          (p) => p.id === targetLabwareParamDefId
        );

        if (targetParamInstanceIndex === -1) {
          console.warn(
            `[decorateParametersWithReplicates] Target labware parameter for replication (ID: ${targetLabwareParamDefId}) not found in step '${
              step.name || step.id
            }'.`
          );
          continue;
        }

        const sourceLabwareParamInstance =
          currentParameters[targetParamInstanceIndex];
        if (
          !sourceLabwareParamInstance.value ||
          typeof sourceLabwareParamInstance.value !== "object" ||
          !("type" in sourceLabwareParamInstance.value) ||
          sourceLabwareParamInstance.value.type !== "labware" ||
          !sourceLabwareParamInstance.value.labware // Ensure labware object exists
        ) {
          console.warn(
            `[decorateParametersWithReplicates] Target parameter '${
              sourceLabwareParamInstance.name
            }' in step '${
              step.name || step.id
            }' is not a valid single labware parameter for replication.`
          );
          continue;
        }

        const originalLabwareValue =
          sourceLabwareParamInstance.value as ProtocolExportParameterValueLabware;
        const originalLabwareObject = originalLabwareValue.labware;

        const allLabwareValuesForTargetParam: ProtocolExportParameterValueLabware[] =
          [originalLabwareValue];

        for (let i = 1; i < numberOfInstances; i++) {
          // Create numberOfInstances - 1 new replicates
          const replicateInstanceNumber = i + 1; // Overall instance number (2, 3, ..., N)
          const newId = `${originalLabwareObject.id}-rep${replicateInstanceNumber}`;
          const newLabel = `${originalLabwareObject.label} (Replicate ${replicateInstanceNumber})`;

          const newReplicateLabwareObject: LabwareObject = {
            id: newId,
            typeId: originalLabwareObject.typeId,
            label: newLabel,
          };
          newGlobalLabware.push(newReplicateLabwareObject);
          allLabwareValuesForTargetParam.push({
            type: "labware",
            labware: newReplicateLabwareObject,
          });
        }

        currentParameters[targetParamInstanceIndex] = {
          ...sourceLabwareParamInstance,
          value: allLabwareValuesForTargetParam,
          // Note: The 'type' field of the parameter instance might still be "labware".
          // Consumers will need to check if 'value' is an array.
        };
      }
    }

    return { ...step, parameters: currentParameters };
  });

  const finalLabwareList = [...protocol.labware];
  newGlobalLabware.forEach((newLw) => {
    if (!finalLabwareList.some((existingLw) => existingLw.id === newLw.id)) {
      finalLabwareList.push(newLw);
    }
  });

  return {
    ...protocol,
    steps: newSteps,
    labware: finalLabwareList,
  };
}

// Re-exported from dedicated module to avoid circular Firebase dependencies in tests
export { getLabwareDimensions, getLabwareTypeDimensionString, parseDimensionString } from "@/automation/utils/labwareDimensions";

// Helper function for decorateProtocolWithDefaultLabwareMetadata
// Tries to get dimensions from step. Returns null if not found or invalid.
function getStepPipettingDimensions(
  stepConfig: SystemConfig["steps"][0]
): [number, number] | null {
  // Check for explicit "no pattern" definition for the step
  if (
    stepConfig?.compatiblePipettingPatterns &&
    stepConfig.compatiblePipettingPatterns.length === 0
  ) {
    return [0, 0]; // Explicitly no pattern, return [0,0]
  }

  // 1. Check stepConfig.compatiblePipettingPatterns
  if (
    stepConfig?.compatiblePipettingPatterns &&
    stepConfig.compatiblePipettingPatterns.length > 0
  ) {
    const patternString = stepConfig.compatiblePipettingPatterns[0];
    const parsed = parseDimensionString(patternString);
    if (parsed) return parsed;
    console.warn(
      `[getStepPipettingDimensions] Invalid compatiblePipettingPatterns format: ${patternString} for step ${
        stepConfig.id || stepConfig.name
      }.`
    );
  }

  return null; // Return null if no valid dimensions found from step
}

/**
 * Decorates labware parameters in a protocol with default metadata (selectedRows, selectedColumns, selectedStartWellAddress)
 * if not already present. Only applies defaults to parameters with role="source" or role="destination".
 * Prioritizes step-specific patterns, then labware-type specific dimensions, and finally global defaults.
 */
export function decorateProtocolWithDefaultLabwareMetadata(
  protocol: ProtocolExport,
  activeConfig: SystemConfig
): ProtocolExport {
  const newSteps = protocol.steps.map((step) => {
    const stepConfig = activeConfig.steps.find(
      (s) => s.id === step.id || s.name === step.name
    );

    if (!stepConfig) {
      console.warn(
        `[DefaultLabwareMetadata] Step config not found for step ${
          step.id || step.name
        }. Skipping metadata decoration for this step.`
      );
      return step;
    }

    // Try to get dimensions from step first
    let stepDims = getStepPipettingDimensions(stepConfig);

    const newParameters = step.parameters.map((param) => {
      // Check if this parameter has a role of "source" or "destination"
      const paramConfig = stepConfig.params.find(
        (p) => p.id === param.id || p.name === param.name
      );

      const hasSourceOrDestinationRole =
        paramConfig?.role === "source" || paramConfig?.role === "destination";

      const processLabwareValue = (
        labwareValue: ProtocolExportParameterValueLabware
      ): ProtocolExportParameterValueLabware => {
        const currentMetadata = labwareValue.metadata || {};
        const needsMetadataUpdate =
          currentMetadata.selectedRows === undefined ||
          currentMetadata.selectedColumns === undefined;

        if (!needsMetadataUpdate) {
          return labwareValue; // No update needed
        }

        // Only apply default metadata if the parameter has a source or destination role
        if (!hasSourceOrDestinationRole) {
          return labwareValue; // Skip metadata decoration for non-source/destination parameters
        }

        let finalRows: number | undefined;
        let finalCols: number | undefined;

        if (stepDims) {
          // Dimensions from step take precedence
          // If stepDims is [0,0] it means explicit "no pattern", so don't set metadata
          if (stepDims[0] > 0 || stepDims[1] > 0) {
            finalRows = stepDims[0];
            finalCols = stepDims[1];
          }
        } else {
          // No step dimensions, try labware-specific dimensions
          const labwareTypeId = labwareValue.labware?.typeId;
          if (labwareTypeId) {
            const dimensionString = getLabwareTypeDimensionString(
              labwareTypeId,
              activeConfig
            );
            const labwareDims = parseDimensionString(dimensionString);
            if (labwareDims && (labwareDims[0] > 0 || labwareDims[1] > 0)) {
              finalRows = labwareDims[0];
              finalCols = labwareDims[1];
            } else if (labwareDims === null && dimensionString) {
              // Invalid format from labware spec
              console.warn(
                `[DefaultLabwareMetadata] Invalid dimensions format '${dimensionString}' for labware type ${labwareTypeId}. Step: ${
                  step.id || step.name
                }. Parameter: ${param.name}. Falling back.`
              );
            }
          }
        }

        // Only update if we found valid dimensions to set
        if (
          finalRows !== undefined &&
          finalCols !== undefined &&
          (finalRows > 0 || finalCols > 0) // Ensure we have valid, positive dimensions
        ) {
          return {
            ...labwareValue,
            metadata: {
              ...currentMetadata,
              selectedRows: finalRows,
              selectedColumns: finalCols,
              selectedStartWellAddresses:
                currentMetadata.selectedStartWellAddresses ||
                ([[1, 1]] as [number, number][]),
            },
          };
        }
        return labwareValue; // Return unchanged if no valid dimensions found or not needed
      };

      if (param.value && typeof param.value === "object") {
        if (Array.isArray(param.value)) {
          const updatedArrayValue = param.value.map((item) => {
            if (
              item &&
              typeof item === "object" &&
              "type" in item &&
              item.type === "labware" &&
              item.labware // ensure labware object exists
            ) {
              return processLabwareValue(
                item as ProtocolExportParameterValueLabware
              );
            }
            return item;
          });
          return { ...param, value: updatedArrayValue };
        } else if (
          "type" in param.value &&
          param.value.type === "labware" &&
          param.value.labware // ensure labware object exists
        ) {
          return {
            ...param,
            value: processLabwareValue(
              param.value as ProtocolExportParameterValueLabware
            ),
          };
        }
      }
      return param;
    });

    return { ...step, parameters: newParameters };
  });

  return { ...protocol, steps: newSteps };
}

/**
 * LEGACY_COMPAT: Derives rows from the "RxC" dimensions spec as a safety net
 * for edge cases where enrichLegacyLabware didn't run (e.g. direct API calls).
 */
function deriveRowsFromSpecs(
  specsRecord: Record<string, string | number | boolean>,
): number | undefined {
  const dim = specsRecord["dimensions"];
  if (dim == null) return undefined;
  const match = String(dim).match(/^(\d+)x(\d+)$/);
  if (match) return parseInt(match[1], 10);
  return undefined;
}

/**
 * Builds labwareConfig, liquidClassDefaults, liquidClassConstraints, and selectedHead
 * from the parsed system config and attaches them to the protocol.
 * This makes the protocol self-contained so the runner doesn't need local files.
 */
export function decorateProtocolWithLabwareConfig(
  protocol: ProtocolExport,
  activeConfig: SystemConfig,
  selectedHeadId?: string | null
): ProtocolExport {
  // Collect all labware typeIds referenced in the protocol
  const referencedTypeIds = new Set<string>();
  for (const lw of protocol.labware) {
    if (lw.typeId) referencedTypeIds.add(lw.typeId);
  }

  // Build labwareConfig from activeConfig.labware (which now has catalog metadata)
  const labwareConfig: Record<string, import("./protocolExport").ProtocolLabwareConfig> = {};
  for (const typeId of referencedTypeIds) {
    const entry = buildLabwareConfigEntry(typeId, activeConfig);
    if (entry) labwareConfig[typeId] = entry;
  }

  // Resolve liquid class defaults and constraints for the selected head
  let liquidClassDefaults: Record<string, Record<string, number | boolean>> | undefined;
  let liquidClassConstraints: Record<string, { min?: number; max?: number }> | undefined;

  if (selectedHeadId && activeConfig.liquidClasses) {
    // Each liquid class in the parsed config has an id (e.g. "aqueous") and specifications array
    // The specs contain both default values and constraint bounds (min/max)
    liquidClassDefaults = {};
    const constraintsMap: Record<string, { min?: number; max?: number }> = {};

    for (const lc of activeConfig.liquidClasses) {
      const defaults: Record<string, number | boolean> = {};
      for (const spec of lc.specifications) {
        // Store default values
        if (typeof spec.value === "number") {
          defaults[spec.name] = spec.value;
        } else if (typeof spec.value === "boolean") {
          defaults[spec.name] = spec.value;
        }
        // Collect constraints (same across all classes for a head)
        if (spec.min != null || spec.max != null) {
          if (!constraintsMap[spec.name]) {
            constraintsMap[spec.name] = {};
          }
          if (spec.min != null) constraintsMap[spec.name].min = spec.min;
          if (spec.max != null) constraintsMap[spec.name].max = spec.max;
        }
      }
      liquidClassDefaults[lc.id] = defaults;
    }

    if (Object.keys(constraintsMap).length > 0) {
      liquidClassConstraints = constraintsMap;
    }
  }

  return {
    ...protocol,
    labwareConfig,
    liquidClassDefaults,
    liquidClassConstraints,
    selectedHead: selectedHeadId ?? undefined,
  };
}

// Export the core decorator functions as well, in case they are needed
// independently or for testing.
export { decorateProtocolWithTips, decorateProtocolWithHiddenLabware };
